import { useState } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { 
  CheckCircle2,
  Clock,
  Circle,
  Loader2,
  Euro,
  Lock,
  ArrowDown,
  Save,
  Calendar as CalendarIcon,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const STEP_STATUS_CONFIG = {
  not_started: { label: "Non démarré", icon: Circle, color: "text-muted-foreground", bg: "bg-muted", border: "border-border" },
  in_progress: { label: "En cours", icon: Loader2, color: "text-blue-600", bg: "bg-blue-500/10", border: "border-blue-500/30" },
  waiting: { label: "En attente", icon: Clock, color: "text-amber-600", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  done: { label: "Terminé", icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-500/10", border: "border-emerald-500/30" }
};

const SUB_STATUS_LABELS = {
  ouvert: "Ouvert",
  en_attente_courrier: "En attente du courrier",
  resolu: "Résolu"
};

const PAYMENT_STEPS = ["prime_cee", "prime_apa", "prime_mpr"];

// Calculate duration between two dates
const getDuration = (date1, date2) => {
  if (!date1 || !date2) return null;
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2 - d1);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return "le même jour";
  if (diffDays === 1) return "1 jour";
  if (diffDays < 7) return `${diffDays} jours`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} semaine${Math.floor(diffDays / 7) > 1 ? 's' : ''}`;
  return `${Math.floor(diffDays / 30)} mois`;
};

const formatShortDate = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export default function EtapesPacSsc({ 
  dossier, 
  savingStep, 
  amountInputs, 
  completionDate,
  onStepStatusChange, 
  onAmountChange, 
  onAmountSave,
  onCompletionDateSave,
  onSousTraitantChange,
  API,
  id,
  fetchDossier,
  isRestricted = false,
  onDevisValidated,
}) {
  // Devis fields state (montant devis, PAC, SSC)
  const [montantDevis, setMontantDevis] = useState(dossier.montant_devis_174 || "");
  const [montantPac, setMontantPac] = useState(dossier.montant_pac || "");
  const [montantSsc, setMontantSsc] = useState(dossier.montant_ssc || "");
  const [savingDevis, setSavingDevis] = useState(false);

  const handleSaveDevisFields = async () => {
    const devis = parseFloat(montantDevis) || 0;
    const pac = parseFloat(montantPac) || 0;
    const ssc = parseFloat(montantSsc) || 0;
    if (devis <= 0) { toast.error("Veuillez saisir le montant du devis"); return; }
    if (!isRestricted) {
      if (pac <= 0) { toast.error("Veuillez saisir le montant de la PAC"); return; }
      if (ssc <= 0) { toast.error("Veuillez saisir le montant du SSC"); return; }
    }
    setSavingDevis(true);
    try {
      const payload = { montant_devis_174: devis };
      if (!isRestricted) {
        payload.montant_pac = pac;
        payload.montant_ssc = ssc;
      }
      // Also update the devis_signe step amount
      payload.montant_devis_mpr = devis;
      await fetch(`${API}/dossiers/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      // Update the step amount too
      onStepStatusChange("devis_signe", "in_progress", { amount: devis });
      toast.success("Montants devis enregistrés");
      setTimeout(() => { if (fetchDossier) fetchDossier(); }, 500);
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingDevis(false);
    }
  };

  // Intercept status change to validate devis requirements
  const handleStepStatusChangeWithValidation = (stepKey, status, sub, amount, compDate) => {
    if (stepKey === "devis_signe" && status === "done") {
      const devis = dossier.montant_devis_174 || parseFloat(montantDevis) || 0;
      if (devis <= 0) { toast.error("Veuillez d'abord saisir et enregistrer le montant du devis"); return; }
      if (!isRestricted) {
        const pac = dossier.montant_pac || parseFloat(montantPac) || 0;
        const ssc = dossier.montant_ssc || parseFloat(montantSsc) || 0;
        if (pac <= 0) { toast.error("Veuillez d'abord saisir et enregistrer le montant de la PAC"); return; }
        if (ssc <= 0) { toast.error("Veuillez d'abord saisir et enregistrer le montant du SSC"); return; }
      }
    }
    onStepStatusChange(stepKey, status, sub, amount, compDate);
  };

  // Check if a step can be modified
  // Steps 1-6 are sequential (0-5 in index)
  // Steps 7, 8, 9 (indices 6, 7, 8) can be validated in any order once step 6 (index 5) is done
  // Step 10 (SAV, index 9) requires all payment steps (7, 8, 9) to be done
  const canModifyStep = (stepIndex) => {
    if (stepIndex === 0) return true;
    
    // Steps 7, 8, 9 (prime_cee, prime_apa, prime_mpr) - indices 6, 7, 8
    // Can be validated in any order once step 6 (dossier_installe, index 5) is done
    if (stepIndex >= 6 && stepIndex <= 8) {
      const step6 = dossier.steps[5]; // dossier_installe
      return step6?.status === 'done';
    }
    
    // Step 10 (SAV, index 9) requires all payment steps to be done
    if (stepIndex === 9) {
      const paymentSteps = [dossier.steps[6], dossier.steps[7], dossier.steps[8]];
      return paymentSteps.every(s => s?.status === 'done');
    }
    
    // Default: previous step must be done (for steps 1-6)
    const prevStep = dossier.steps[stepIndex - 1];
    return prevStep?.status === 'done';
  };

  return (
    <div className="border border-border rounded-sm bg-card">
      <div className="divide-y divide-border">
        {dossier.steps?.map((step, index) => {
          const stepStatusConfig = STEP_STATUS_CONFIG[step.status] || STEP_STATUS_CONFIG.not_started;
          const StepStatusIcon = stepStatusConfig.icon;
          const isSaving = savingStep === step.key;
          const hasSubStatuses = ['compte_mpr', 'sav'].includes(step.key);
          const isPaymentStep = PAYMENT_STEPS.includes(step.key);
          const isInstallStep = step.key === 'dossier_installe';
          const isLocked = !canModifyStep(index);
          
          // Get previous step for timeline
          const prevValidatedStep = index > 0 ? dossier.steps.slice(0, index).reverse().find(s => s.status === 'done' && s.updated_at) : null;
          const duration = prevValidatedStep && step.status === 'done' && step.updated_at
            ? getDuration(prevValidatedStep.updated_at, step.updated_at)
            : null;
          
          return (
            <div 
              key={step.key}
              className={cn("p-4", isLocked && "opacity-60")}
              data-testid={`step-${step.key}`}
            >
              {/* Timeline duration indicator */}
              {duration && (
                <div className="flex items-center gap-2 mb-2 ml-12 text-xs text-muted-foreground">
                  <ArrowDown className="w-3 h-3" />
                  <span className="bg-muted px-2 py-0.5 rounded-sm">{duration}</span>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1">
                  <div className={cn(
                    "flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium",
                    step.status === 'done' ? "bg-emerald-500/20 text-emerald-600 dark:text-emerald-400" : "bg-muted"
                  )}>
                    {step.status === 'done' ? <CheckCircle2 className="w-4 h-4" /> : index + 1}
                  </div>
                  <div className={cn(
                    "w-10 h-10 rounded-sm flex items-center justify-center shrink-0",
                    stepStatusConfig.bg
                  )}>
                    {isLocked ? (
                      <Lock className="w-5 h-5 text-muted-foreground" />
                    ) : (
                      <StepStatusIcon className={cn("w-5 h-5", stepStatusConfig.color, step.status === 'in_progress' && "animate-spin")} />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{step.name}</p>
                    {step.sub_status && (
                      <p className="text-sm text-muted-foreground">
                        {SUB_STATUS_LABELS[step.sub_status] || step.sub_status}
                      </p>
                    )}
                    {step.updated_at && step.status === 'done' && (
                      <p className="text-xs text-emerald-600 mt-1">
                        Validé le {formatShortDate(step.updated_at)}
                      </p>
                    )}
                    {isLocked && (
                      <p className="text-xs text-amber-600 mt-1">
                        {index >= 6 && index <= 8 
                          ? "Terminez d'abord l'étape \"Travaux terminés\""
                          : index === 9 
                            ? "Terminez d'abord toutes les étapes de paiement (CEE, APA, MPR)"
                            : "Terminez d'abord l'étape précédente"
                        }
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  {/* Devis fields for devis_signe step (montant devis + PAC + SSC) */}
                  {step.key === 'devis_signe' && (
                    <div className="flex flex-col gap-2 p-3 border border-border rounded-sm bg-muted/30">
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <Euro className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                          <Input type="number" placeholder="Montant devis" value={montantDevis}
                            onChange={(e) => setMontantDevis(e.target.value)} disabled={isLocked}
                            className="h-7 pl-7 text-xs w-[120px] rounded-sm text-right" data-testid="devis-montant-input" />
                        </div>
                        {!isRestricted && (
                        <>
                        <div className="relative flex-1">
                          <Input type="number" placeholder="PAC" value={montantPac}
                            onChange={(e) => setMontantPac(e.target.value)} disabled={isLocked}
                            className="h-7 text-xs w-[90px] rounded-sm text-right" data-testid="devis-pac-input" />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground pointer-events-none">PAC</span>
                        </div>
                        <div className="relative flex-1">
                          <Input type="number" placeholder="SSC" value={montantSsc}
                            onChange={(e) => setMontantSsc(e.target.value)} disabled={isLocked}
                            className="h-7 text-xs w-[90px] rounded-sm text-right" data-testid="devis-ssc-input" />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground pointer-events-none">SSC</span>
                        </div>
                        </>
                        )}
                        <Button variant="outline" size="sm" onClick={handleSaveDevisFields}
                          disabled={savingDevis || isLocked} className="rounded-sm h-7 px-2" data-testid="save-devis-fields">
                          {savingDevis ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        </Button>
                      </div>
                      {(!dossier.montant_devis_174) && !isLocked && (
                        <p className="text-[10px] text-amber-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {isRestricted ? "Remplissez le montant du devis avant de valider" : "Remplissez le montant du devis, PAC et SSC avant de valider"}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Date picker for dossier_installe */}
                  {isInstallStep && (
                    <>
                      <Select
                        value={dossier.sous_traitant || ""}
                        onValueChange={onSousTraitantChange}
                        disabled={isLocked || isSaving}
                      >
                        <SelectTrigger className="w-[120px] rounded-sm" data-testid="sous-traitant-select">
                          <SelectValue placeholder="Poseur" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="STEVEN">STEVEN</SelectItem>
                          <SelectItem value="VALENTIN">VALENTIN</SelectItem>
                        </SelectContent>
                      </Select>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            disabled={isLocked || isSaving}
                            className={cn(
                              "w-[140px] justify-start text-left font-normal rounded-sm",
                              !completionDate && "text-muted-foreground"
                            )}
                            data-testid="completion-date-picker"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {completionDate ? format(completionDate, "dd/MM/yyyy") : "Date travaux"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={completionDate}
                            onSelect={onCompletionDateSave}
                            locale={fr}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </>
                  )}
                  
                  {/* Amount input for payment steps */}
                  {isPaymentStep && (
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Euro className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="number"
                          placeholder="Montant"
                          value={amountInputs[step.key] || ""}
                          onChange={(e) => onAmountChange(step.key, e.target.value)}
                          disabled={isLocked}
                          className="w-[120px] pl-9 rounded-sm text-right"
                          data-testid={`amount-input-${step.key}`}
                        />
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onAmountSave(step.key)}
                        disabled={isSaving || isLocked}
                        className="rounded-sm"
                        data-testid={`save-amount-${step.key}`}
                      >
                        <Save className="w-4 h-4" />
                      </Button>
                    </div>
                  )}

                  <Select
                    value={step.status}
                    onValueChange={(value) => handleStepStatusChangeWithValidation(step.key, value, null, null, null)}
                    disabled={isSaving || isLocked}
                  >
                    <SelectTrigger 
                      className={cn(
                        "w-[160px] rounded-sm",
                        stepStatusConfig.bg,
                        stepStatusConfig.border,
                        "border"
                      )}
                      data-testid={`step-status-select-${step.key}`}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">
                        <div className="flex items-center gap-2">
                          <Circle className="w-4 h-4 text-muted-foreground" />
                          Non démarré
                        </div>
                      </SelectItem>
                      <SelectItem value="in_progress">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 text-blue-600" />
                          En cours
                        </div>
                      </SelectItem>
                      <SelectItem value="waiting">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-amber-600" />
                          En attente
                        </div>
                      </SelectItem>
                      <SelectItem value="done">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          Terminé
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {/* Sub-status for specific steps */}
                  {hasSubStatuses && step.key === 'compte_mpr' && (
                    <Select
                      value={step.sub_status || ""}
                      onValueChange={(value) => onStepStatusChange(step.key, step.status, value, null, null)}
                      disabled={isSaving || isLocked}
                    >
                      <SelectTrigger className="w-[180px] rounded-sm" data-testid={`step-substatus-${step.key}`}>
                        <SelectValue placeholder="Sous-statut..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ouvert">Ouvert</SelectItem>
                        <SelectItem value="en_attente_courrier">En attente du courrier</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  
                  {hasSubStatuses && step.key === 'sav' && (
                    <Select
                      value={step.sub_status || ""}
                      onValueChange={(value) => onStepStatusChange(step.key, step.status, value, null, null)}
                      disabled={isSaving || isLocked}
                    >
                      <SelectTrigger className="w-[140px] rounded-sm" data-testid={`step-substatus-${step.key}`}>
                        <SelectValue placeholder="Sous-statut..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ouvert">Ouvert</SelectItem>
                        <SelectItem value="resolu">Résolu</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
